import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FilterPopover from '../../community/residents/components/FilterPopover.jsx';

/**
 * Formats a datetime ISO string to relative time ("3m ago", "5h ago", "2d ago").
 * Falls back to locale date string for dates > 30 days.
 */
function relativeTime(isoString) {
  if (!isoString) return '\u2014';
  const now = new Date();
  const then = new Date(isoString);
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return then.toLocaleDateString();
}

function fullDateTime(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString();
}

// ---------------------------------------------------------------------------
// Status popover — wraps a cell value and shows detail on hover
// ---------------------------------------------------------------------------
const StatusPopover = ({ children, message }) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);
  const hoverTimeout = useRef(null);

  if (!message) return children;

  const handleEnter = () => {
    clearTimeout(hoverTimeout.current);
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popoverW = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    let left = centerX - popoverW / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));

    setStyle({
      position: 'fixed',
      zIndex: 9999,
      left: `${left}px`,
      top: spaceBelow < 100 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 100 ? `${window.innerHeight - rect.top + 6}px` : undefined,
      width: `${popoverW}px`,
      whiteSpace: 'normal',
    });
    setOpen(true);
  };

  const handleLeave = () => {
    hoverTimeout.current = setTimeout(() => setOpen(false), 100);
  };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  return (
    <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={handleLeave} style={{ position: 'relative', display: 'inline-block' }}>
      {children}
      {open ? (
        <span className="na-type-popover" style={style}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{message}</span>
        </span>
      ) : null}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Column definitions — Cloud campus (9 columns)
// ---------------------------------------------------------------------------
export const SERVER_CLOUD_COLUMN_DEFS = [
  { key: 'is_offline', label: 'Online Status', sortable: true, filterable: true },
  { key: 'description', label: 'Description', sortable: true, filterable: true },
  { key: 'palcare_interface_name', label: 'Service', sortable: true, filterable: true },
  { key: 'software_version', label: 'Version', sortable: true, filterable: true },
  { key: 'area_name', label: 'Area', sortable: true, filterable: true },
  { key: 'ip_address', label: 'IP Address', sortable: true, filterable: true },
  { key: 'mac_addr', label: 'Mac Address', sortable: true, filterable: true },
  { key: 'is_active', label: 'Implementation', sortable: true, filterable: true },
  { key: 'last_heard', label: 'Last Update', sortable: true, filterable: false },
];

// ---------------------------------------------------------------------------
// Column definitions — Non-cloud campus (6 columns)
// ---------------------------------------------------------------------------
export const SERVER_COLUMN_DEFS = [
  { key: 'description', label: 'Description', sortable: true, filterable: true },
  { key: 'palcare_interface_name', label: 'Service', sortable: true, filterable: true },
  { key: 'area_name', label: 'Area', sortable: true, filterable: true },
  { key: 'ip_address', label: 'IP Address', sortable: true, filterable: true },
  { key: 'mac_addr', label: 'Mac Address', sortable: true, filterable: true },
  { key: 'is_active', label: 'Status', sortable: true, filterable: true },
];

/**
 * ServersTable — full data table for PalCare server devices with
 * sorting, filtering, sticky header, row selection, and row actions.
 */
const ServersTable = ({
  data,
  allData,
  visibleColumns,
  columnDefs,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onDeleteDevice,
  deleteRights,
}) => {
  const activeColumns = columnDefs.filter((col) =>
    visibleColumns.includes(col.key)
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = (columnKey) => {
    const values = new Set();
    (allData || data).forEach((row) => {
      const val = row[columnKey];
      if (columnKey === 'is_active') {
        values.add(val ? 'Active' : 'Backup');
      } else if (columnKey === 'is_offline') {
        values.add(val === null ? 'Unknown' : !!val === true ? 'Offline' : 'Online');
      } else {
        values.add(val != null && val !== '' ? String(val) : '(empty)');
      }
    });
    return [...values].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  const showCheckbox = deleteRights;
  const allSelected =
    data.length > 0 && data.every((r) => selectedIds.includes(r.palcare_device_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg
          className="dp-icon-sm dp-sort-icon dp-sort-icon--neutral"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
          />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.5 15.75l7.5-7.5 7.5 7.5"
        />
      </svg>
    ) : (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'description':
        return row.description && row.palcare_device_id ? (
          <Link
            to={'/community/server_devices/' + row.palcare_device_id}
            className="dp-device-link"
            style={{ fontWeight: 'var(--font-weight-semibold)' }}
          >
            {row.description}
          </Link>
        ) : (
          <span className="dp-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            {row.description || '\u2014'}
          </span>
        );

      case 'area_name':
        return row.area_id ? (
          <Link to={'/community/areas/' + row.area_id} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>
            {row.area_name}
          </Link>
        ) : (
          <span className="dp-cell-text">{row.area_name || '—'}</span>
        );

      case 'mac_addr':
        return row.mac_addr && row.palcare_device_id ? (
          <Link
            to={'/community/server_devices/' + row.palcare_device_id}
            className="dp-device-link"
          >
            {row.mac_addr}
          </Link>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      case 'is_active':
        return row.is_active ? (
          <span className="dp-status dp-status--active">
            <span className="dp-status__dot" aria-hidden="true" />
            Active
          </span>
        ) : (
          <span className="dp-status dp-status--inactive">
            <span className="dp-status__dot" aria-hidden="true" />
            Backup
          </span>
        );

      case 'is_offline':
        // Three-state: false = Online, true = Offline, null = Unknown
        return row.is_offline === null ? (
            <span className="dp-status dp-status--warning">
            <span className="dp-status__dot" aria-hidden="true" />
            Unknown
          </span>
        ) : !!row.is_offline === true ? (
          <span className="dp-status dp-status--inactive">
            <span className="dp-status__dot" aria-hidden="true" />
            Offline
          </span>
        ) : (
          <span className="dp-status dp-status--active">
            <span className="dp-status__dot" aria-hidden="true" />
            Online
          </span>
        );

      case 'last_heard':
        return row.last_heard ? (
          <StatusPopover message={fullDateTime(row.last_heard)}>
            <span className={`dp-cell-text dp-cell-text--secondary${row?.last_heard ? " has-popover" : ""}`}>
              {relativeTime(row.last_heard)}
            </span>
          </StatusPopover>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      default:
        return (
          <span className="dp-cell-text">{row[columnKey] || '\u2014'}</span>
        );
    }
  };

  return (
    <div className="dp-table-wrap">
      <table className="dp-table">
        <thead className="dp-thead">
          <tr>
            {/* Checkbox column */}
            {showCheckbox ? (
              <th scope="col" className="dp-th dp-th--checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={onSelectAll}
                  className="dp-checkbox"
                  aria-label="Select all servers"
                />
              </th>
            ) : null}

            {/* Actions column */}
            <th scope="col" className="dp-th dp-th--actions">
              <span className="dp-sr-only">Actions</span>
            </th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="dp-th">
                <div className="dp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="dp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.filterKey || col.key}
                      label={col.label}
                      values={getFilterValues(col.filterKey || col.key)}
                      selectedValues={filters[col.filterKey || col.key] || []}
                      onChange={onFilterChange}
                      data={allData}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="dp-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.palcare_device_id);
            return (
              <tr
                key={row.palcare_device_id}
                className={`dp-tr ${isSelected ? 'is-selected' : ''}`}
              >
                {/* Checkbox */}
                {showCheckbox ? (
                  <td className="dp-td dp-td--checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectRow(row.palcare_device_id)}
                      className="dp-checkbox"
                      aria-label={`Select ${row.description}`}
                    />
                  </td>
                ) : null}

                {/* Row actions kebab */}
                <td className="dp-td dp-td--actions">
                  <ServerRowActionsMenu
                    server={row}
                    deleteRights={deleteRights}
                    onDeleteDevice={onDeleteDevice}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="dp-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * ServerRowActionsMenu — kebab menu for per-row server actions.
 * View Details (always) + Delete (when user has rights).
 */
const ServerRowActionsMenu = ({
  server,
  deleteRights,
  onDeleteDevice,
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  const handleToggle = () => {
    if (!open && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < 180) {
        setMenuStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, right: `${window.innerWidth - rect.right}px`, top: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
      } else {
        setMenuStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, right: `${window.innerWidth - rect.right}px`, bottom: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
      }
    }
    setOpen(!open);
  };

  return (
    <div className="dp-row-actions" ref={menuRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="dp-row-actions__trigger"
        aria-label={`Actions for ${server.description}`}
        aria-expanded={open}
      >
        <i className="fe fe-more-vertical" aria-hidden="true" />
      </button>

      {open ? (
        <div className="dp-row-actions__menu" style={menuStyle}>
          {/* View Details — links to ServerProfilePage */}
          <Link
            to={`/community/server_devices/${server.palcare_device_id}`}
            className="dp-row-actions__item"
            onClick={() => setOpen(false)}
          >
            <svg
              className="dp-icon-md dp-row-actions__item-icon"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            View Details
          </Link>

          {/* Delete Server — only when user has rights */}
          {deleteRights ? (
            <button
              type="button"
              onClick={() => {
                onDeleteDevice(server);
                setOpen(false);
              }}
              className="dp-row-actions__item dp-row-actions__item--danger"
            >
              <svg
                className="dp-icon-md dp-row-actions__item-icon"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                />
              </svg>
              Delete Server
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ServersTable;
